import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Button } from "../../redesign";
import { FormSection, FieldBlock, FieldRow, TextInput, ToggleSwitch, UploadTile, AudioTile } from "../../redesign/edit";
import { preventEnterFormSubmission } from "../../../utils/formUtils";
import { copyToClipboard } from "../../../utils/clipboard";
import PlayerAssetPreviewCard from "./PlayerAssetPreviewCard";
import { fetchPlayerAsset, savePlayerAsset, deletePlayerAsset } from "../../../actions/playerAssetActions";
import { uploadImage, uploadAudio, clearMedia } from "../../../actions/mediaActions";
import {
  SLUG_PATTERN,
  SLUG_MAX_LENGTH,
  NAME_MAX_LENGTH,
  ANALYTICS_NAME_MAX_LENGTH,
  slugify,
  toPayload,
  changedFields,
  UNCONFIRMED_MESSAGE,
} from "../../../constants/playerAssetTypes";
import LoadingBackdrop from "../../Common/LoadingBackdrop";

const EMPTY_STATE = {
  name: "",
  slug: "",
  analytics_name: "",
  is_active: true,
  is_default: false,
  image_url: "",
  thumbnail_url: "",
  wide_image_url: "",
  audio_url: "",
  isSetToEdit: false,
};

export const PLAYER_ASSET_FORM_ID = "player-asset-form";

const MEDIA_FIELDS = ["image_url", "thumbnail_url", "wide_image_url", "audio_url"];

// Local object URLs let the preview show a picked file while it uploads.
// jsdom (tests) has no createObjectURL, so this degrades to "no preview".
function createLocalPreview(file) {
  try {
    return typeof URL.createObjectURL === "function" ? URL.createObjectURL(file) : null;
  } catch {
    return null;
  }
}

function revokeLocalPreview(url) {
  if (url && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(url);
}

// A small "Copy URL" action under a hosted media tile (kept from the v1 uploaders).
function CopyUrlButton({ url }) {
  const [copied, setCopied] = useState(false);
  if (!url || url.startsWith("blob:")) return null;
  return (
    <button
      type="button"
      onClick={() => {
        copyToClipboard(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      title={url}
      className="font-condensed font-bold uppercase bg-transparent border-0 cursor-pointer text-teal-green-dark hover:text-navy"
      style={{ fontSize: 10.5, letterSpacing: "0.08em", padding: "6px 0 0", alignSelf: "flex-start" }}
    >
      {copied ? "Copied" : "Copy URL"}
    </button>
  );
}

// Upload tile preview shape per image slot, close to how the app shows it.
function uploadTileShape(type, key) {
  if (key === "wideImage") return { ratio: "16 / 9", maxWidth: 320 };
  if (type === "background") return { ratio: "3 / 4", maxWidth: 220 };
  if (type === "background_music") return { ratio: "220 / 134", maxWidth: 320 };
  return { ratio: "1 / 1", maxWidth: 220 };
}

function HelperText({ children }) {
  return (
    <p className="text-text-muted" style={{ fontSize: 11.5, margin: "6px 0 0", lineHeight: 1.45 }}>
      {children}
    </p>
  );
}

const AddEditPlayerAsset = forwardRef(({ isEdit, selectedId, config, onLoaded }, ref) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { fields } = config;

  const [state, setState] = useState(EMPTY_STATE);
  // Per media field: a local preview URL + whether its upload is in flight.
  const [localPreviews, setLocalPreviews] = useState({});
  const [uploading, setUploading] = useState({});
  const localPreviewsRef = useRef({});
  localPreviewsRef.current = localPreviews;
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  const [loading, setLoading] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [analyticsNameTouched, setAnalyticsNameTouched] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Synchronous re-entrancy lock, same reasoning as AddEditBackgroundMusic:
  // a second Save click while an async submit is in flight must not double-save.
  const isSubmittingRef = useRef(false);

  // The payload as loaded from the server; an edit sends only what differs.
  const loadedPayloadRef = useRef(null);

  const loadAsset = useCallback(() => {
    if (!isEdit || !selectedId) return;

    setLoading(true);
    setLoadError(null);
    dispatch(fetchPlayerAsset(selectedId))
      .then(asset => {
        const loaded = {
          name: asset.name || "",
          slug: asset.slug || "",
          analytics_name: asset.analytics_name || "",
          is_active: !!asset.is_active,
          is_default: !!asset.is_default,
          image_url: asset.image_url || "",
          thumbnail_url: asset.thumbnail_url || "",
          wide_image_url: asset.wide_image_url || "",
          audio_url: asset.audio_url || "",
          isSetToEdit: true,
        };
        loadedPayloadRef.current = toPayload(loaded);
        setState(loaded);
        onLoadedRef.current?.({ name: loaded.name, slug: loaded.slug });
      })
      .catch(error => {
        // Without this the form would fall back to its empty state and Update
        // would write those blanks over the existing asset's media and flags.
        console.error("Error fetching player asset:", error);
        setLoadError(error?.response?.data?.message || "Could not load this asset.");
      })
      .finally(() => setLoading(false));
  }, [dispatch, isEdit, selectedId]);

  useEffect(() => {
    loadAsset();
  }, [loadAsset]);

  const handleNameChange = e => {
    const name = e.target.value;
    setState(prev => ({
      ...prev,
      name,
      // Only auto-suggest on create, and only until the admin edits the slug themselves.
      slug: !isEdit && !slugTouched ? slugify(name) : prev.slug,
      // Auto-fill only until the admin types an analytics name. Comparing it
      // with the previous display name is not proof it is untouched: renaming
      // to the value the admin chose would start overwriting it again.
      analytics_name: !isEdit && !analyticsNameTouched ? name : prev.analytics_name,
    }));
  };

  const handleSlugChange = e => {
    setSlugTouched(true);
    setState(prev => ({ ...prev, slug: e.target.value }));
  };

  const handleAnalyticsNameChange = e => {
    setAnalyticsNameTouched(true);
    setState(prev => ({ ...prev, analytics_name: e.target.value }));
  };

  // The upload actions open a blocking "Loading..." dialog with no dismiss
  // control, and their error handler shows nothing when the request never
  // reached the server (no error.response). Replacing that dialog here is what
  // frees the admin.
  const showUploadError = error => {
    Swal.fire({
      icon: "error",
      title: "Upload failed",
      text: error?.response?.data?.message || "The file could not be uploaded. Please try again.",
    });
  };

  const handleSwitchChange = field => e => {
    setState(prev => ({ ...prev, [field]: e.target.checked }));
  };

  const setLocalPreview = (field, url) => {
    setLocalPreviews(prev => {
      if (prev[field] && prev[field] !== url) revokeLocalPreview(prev[field]);
      return { ...prev, [field]: url };
    });
  };

  // Revoke whatever local previews are still alive when the form goes away.
  useEffect(() => () => Object.values(localPreviewsRef.current).forEach(revokeLocalPreview), []);

  const startUpload = (field, file, upload, urlFromResponse) => {
    const preview = createLocalPreview(file);
    setLocalPreview(field, preview);
    setUploading(prev => ({ ...prev, [field]: true }));

    // Settle the upload flag in the same tick as the URL (one batched render),
    // so the form never shows the hosted file while still counting it as
    // uploading. Only clear this upload's own preview: a newer pick may have
    // replaced it.
    const finish = () => {
      setLocalPreviews(prev => {
        if (prev[field] !== preview) return prev;
        revokeLocalPreview(preview);
        return { ...prev, [field]: null };
      });
      setUploading(prev => ({ ...prev, [field]: false }));
    };

    dispatch(upload(file))
      .then(response => {
        setState(prev => ({ ...prev, [field]: urlFromResponse(response) }));
        finish();
        dispatch(clearMedia());
      })
      .catch(error => {
        finish();
        showUploadError(error);
      });
  };

  const makeImageUploadHandler = field => e => {
    const file = e.target.files[0];
    if (!file) return;
    startUpload(field, file, uploadImage, response => response.data.image_url);
  };

  const handleAudioUpload = e => {
    const file = e.target.files[0];
    if (!file) return;
    startUpload("audio_url", file, uploadAudio, response => response.data.audio_file_url);
  };

  const makeRemoveHandler = field => () => {
    setLocalPreview(field, null);
    setState(prev => ({ ...prev, [field]: "" }));
  };

  // What the tiles and the preview show: the picked file while it uploads,
  // else the hosted URL.
  const mediaValue = field => localPreviews[field] || state[field];
  const isUploading = MEDIA_FIELDS.some(field => uploading[field]);

  const validate = useCallback(() => {
    if (!state.name.trim()) return "Please enter a name";
    if (state.name.length > NAME_MAX_LENGTH) return `Name must be ${NAME_MAX_LENGTH} characters or fewer`;
    if (!state.analytics_name.trim()) return "Please enter an analytics name";
    if (state.analytics_name.length > ANALYTICS_NAME_MAX_LENGTH) {
      return `Analytics name must be ${ANALYTICS_NAME_MAX_LENGTH} characters or fewer`;
    }
    if (!isEdit) {
      if (!state.slug.trim()) return "Please enter a slug";
      if (state.slug.length > SLUG_MAX_LENGTH) return `Slug must be ${SLUG_MAX_LENGTH} characters or fewer`;
      if (!SLUG_PATTERN.test(state.slug)) {
        return "Slug must be lowercase letters, numbers, and single hyphens (e.g. calm-blue)";
      }
    }
    if (fields.image.required && !state.image_url) return `Please upload ${fields.image.label.toLowerCase()}`;
    if (fields.audio.show && fields.audio.required && !state.audio_url) return "Please upload an audio file";
    return null;
  }, [state, isEdit, fields]);

  const submit = useCallback(async () => {
    if (isSubmittingRef.current) return;

    // Never send a form that was never filled from the server: it would clear
    // every field the admin cannot see.
    if (isEdit && !state.isSetToEdit) {
      Swal.fire("Error", "This asset has not loaded yet. Reload the page before saving.", "error");
      return;
    }

    if (isUploading) {
      Swal.fire("Error", "Please wait for the upload to finish", "error");
      return;
    }

    const validationError = validate();
    if (validationError) {
      Swal.fire("Error", validationError, "error");
      return;
    }

    isSubmittingRef.current = true;

    try {
      const payload = toPayload(state);
      const data = isEdit ? changedFields(payload, loadedPayloadRef.current || payload) : payload;

      if (isEdit && Object.keys(data).length === 0) {
        Swal.fire({ icon: "info", title: "No changes to save", timer: 1200 });
        return;
      }

      if (!isEdit) {
        data.type = config.type;
        data.slug = state.slug;
      }

      await dispatch(savePlayerAsset(data, isEdit ? selectedId : undefined));

      Swal.fire({ icon: "success", title: "Saved!", timer: 1000 }).then(() => {
        navigate(config.basePath);
      });
    } catch (error) {
      console.error("Error saving player asset:", error);
      if (!error?.response) {
        Swal.fire({ icon: "error", title: "Save not confirmed", text: UNCONFIRMED_MESSAGE });
      }
    } finally {
      isSubmittingRef.current = false;
    }
  }, [state, isEdit, selectedId, dispatch, navigate, validate, config, isUploading]);

  const handleDelete = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    }).then(result => {
      if (result.isConfirmed) {
        dispatch(deletePlayerAsset(selectedId))
          .then(() => {
            Swal.fire({ icon: "success", title: `${config.singular} Deleted!`, timer: 1000 }).then(() => {
              navigate(config.basePath);
            });
          })
          .catch(error => {
            // A server response was already shown by the shared handler.
            if (!error?.response) {
              Swal.fire({ icon: "error", title: "Delete not confirmed", text: UNCONFIRMED_MESSAGE });
            }
          });
      }
    });
  };

  useImperativeHandle(ref, () => ({ submit }), [submit]);

  if (isEdit && !state.isSetToEdit && loading) {
    return <LoadingBackdrop open={true} />;
  }

  if (isEdit && loadError) {
    return (
      <div
        className="bg-surface border border-sand flex flex-col items-center text-center"
        style={{ borderRadius: 12, padding: "32px 16px", gap: 12, maxWidth: 560 }}
      >
        <p className="text-danger" style={{ fontSize: 13.5, margin: 0 }}>
          {loadError}
        </p>
        <Button variant="primary" onClick={loadAsset}>
          Retry
        </Button>
      </div>
    );
  }

  const handleFormSubmit = e => {
    e.preventDefault();
    submit();
  };

  const nameValue = state.name;

  return (
    <div className="edit-canvas grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] items-start" style={{ gap: 24 }}>
      <form
        id={PLAYER_ASSET_FORM_ID}
        onSubmit={handleFormSubmit}
        onKeyDown={preventEnterFormSubmission}
        autoComplete="off"
        style={{ minWidth: 0 }}
      >
        <FormSection title="Details" num="01">
          <FieldRow cols={2} className="items-start">
            <FieldBlock label="Name" required htmlFor="player-asset-name">
              <TextInput id="player-asset-name" name="name" value={nameValue} onChange={handleNameChange} required />
            </FieldBlock>
            <FieldBlock label="Slug" required={!isEdit} htmlFor="player-asset-slug">
              <TextInput
                id="player-asset-slug"
                name="slug"
                className="font-mono"
                value={state.slug}
                onChange={handleSlugChange}
                disabled={isEdit}
                required={!isEdit}
                style={isEdit ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
              />
              <HelperText>
                {isEdit ? "Slug is permanent — apps store it." : "Lowercase letters, numbers, and hyphens only."}
              </HelperText>
            </FieldBlock>
          </FieldRow>
          <FieldRow cols={1} className="items-start">
            <FieldBlock label="Analytics name" required htmlFor="player-asset-analytics-name" noMargin>
              <TextInput
                id="player-asset-analytics-name"
                name="analytics_name"
                value={state.analytics_name}
                onChange={handleAnalyticsNameChange}
                required
              />
              <HelperText>Value sent to Amplitude. Changing it breaks chart continuity.</HelperText>
            </FieldBlock>
          </FieldRow>
        </FormSection>

        <FormSection title="Media" num="02">
          <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: 16 }}>
            {fields.audio.show && (
              <div className="flex flex-col" style={{ minWidth: 0 }}>
                <AudioTile
                  label={fields.audio.label}
                  required={fields.audio.required}
                  name="audio_url"
                  inputId="player-asset-audio_url"
                  value={mediaValue("audio_url")}
                  uploading={!!uploading.audio_url}
                  onChange={handleAudioUpload}
                  onRemove={uploading.audio_url ? undefined : makeRemoveHandler("audio_url")}
                />
                <CopyUrlButton url={state.audio_url} />
              </div>
            )}

            {[
              ["image", "image_url"],
              ["thumbnail", "thumbnail_url"],
              ["wideImage", "wide_image_url"],
            ]
              .filter(([key]) => fields[key].show)
              .map(([key, field]) => (
                <div key={field} className="flex flex-col" style={{ minWidth: 0 }}>
                  <UploadTile
                    label={fields[key].label}
                    required={fields[key].required}
                    name={field}
                    inputId={`player-asset-${field}`}
                    value={mediaValue(field)}
                    uploading={!!uploading[field]}
                    previewAspectRatio={uploadTileShape(config.type, key).ratio}
                    maxWidth={uploadTileShape(config.type, key).maxWidth}
                    onChange={makeImageUploadHandler(field)}
                    onRemove={uploading[field] ? undefined : makeRemoveHandler(field)}
                  />
                  {fields[key].helperText && <HelperText>{fields[key].helperText}</HelperText>}
                  <CopyUrlButton url={state[field]} />
                </div>
              ))}
          </div>
        </FormSection>

        <FormSection title="Visibility" num="03">
          <div className="flex flex-col" style={{ gap: 14 }}>
            <ToggleSwitch
              name="is_active"
              checked={state.is_active}
              onChange={handleSwitchChange("is_active")}
              label="Active"
              hint="Inactive assets are hidden from the app."
            />
            <ToggleSwitch
              name="is_default"
              checked={state.is_default}
              onChange={handleSwitchChange("is_default")}
              label="Default"
              hint="Only one default per type."
            />
          </div>
        </FormSection>

        {isEdit && (
          <FormSection title="Danger zone" num="04">
            <div className="flex items-center" style={{ gap: 12, flexWrap: "wrap" }}>
              <Button variant="danger" onClick={handleDelete}>
                Delete
              </Button>
              <span className="text-text-muted" style={{ fontSize: 12 }}>
                Permanent — cannot be undone.
              </span>
            </div>
          </FormSection>
        )}

        <LoadingBackdrop open={loading} />
      </form>

      <aside
        className="edit-rail"
        style={{ alignSelf: "start", display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}
      >
        <PlayerAssetPreviewCard
          config={config}
          asset={{
            name: state.name,
            image: fields.image.show ? mediaValue("image_url") : "",
            thumbnail: fields.thumbnail.show ? mediaValue("thumbnail_url") : "",
            wideImage: fields.wideImage.show ? mediaValue("wide_image_url") : "",
            audio: fields.audio.show ? mediaValue("audio_url") : "",
          }}
        />
      </aside>
    </div>
  );
});

AddEditPlayerAsset.displayName = "AddEditPlayerAsset";

export default AddEditPlayerAsset;
